import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card,
  Select,
  Option
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import StockDetailLayout from './StockDetailLayout';
import ResponsiveDetailTable from './ResponsiveDetailTable';
import DetailFieldCard from './DetailFieldCard';
import VendorSelect from './VendorSelect';
import AddVendorModal from './AddVendorModal';

const today = () => new Date().toISOString().slice(0, 10);
const nowDateTimeLocal = () => {
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${date}T${time}`;
};

const DisposablePopup = ({ disposableId, onClose, variant = 'modal' }) => {
  const isPage = variant === 'page';
  const [disposable, setDisposable] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddQty, setShowAddQty] = useState(false);
  const [addQtyForm, setAddQtyForm] = useState({ quantity: '', date_entered: today(), rate: '', vendor_id: '' });
  const [addQtySubmitting, setAddQtySubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ disposable_name: '', rate: '', date_entered: '', vendor_id: '', quantity: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [consumeModalOpen, setConsumeModalOpen] = useState(false);
  const [consumeForm, setConsumeForm] = useState({ qty: '', datetime: nowDateTimeLocal() });
  const [consuming, setConsuming] = useState(false);
  const [editingHistoryItem, setEditingHistoryItem] = useState(null);
  const [historyEditForm, setHistoryEditForm] = useState({ changeAmount: '', date: '', time: '00:00', changeNote: '' });
  const [historyEditSubmitting, setHistoryEditSubmitting] = useState(false);
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState(null);

  const fetchDisposable = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/disposables/${disposableId}`);
      setDisposable(response.data.disposable);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch disposable');
    } finally {
      setLoading(false);
    }
  }, [disposableId]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/disposables/${disposableId}/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, [disposableId]);

  const fetchVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  const handleVendorCreated = (vendor) => {
    setVendors((prev) => [...prev, vendor]);
    setAddQtyForm((prev) => ({ ...prev, vendor_id: String(vendor.id) }));
    setAddVendorOpen(false);
  };

  useEffect(() => {
    fetchDisposable();
    fetchHistory();
  }, [fetchDisposable, fetchHistory]);

  useEffect(() => {
    if ((showAddQty || editing) && vendors.length === 0) {
      fetchVendors();
    }
  }, [showAddQty, editing, vendors.length, fetchVendors]);

  const handleAddQtySubmit = async (e) => {
    e?.preventDefault();
    const qty = parseInt(addQtyForm.quantity, 10);
    if (!addQtyForm.date_entered || !qty || qty <= 0) {
      setError('Purchase date and quantity are required');
      return;
    }
    if (!addQtyForm.vendor_id) {
      setError('Vendor is required');
      return;
    }
    try {
      setAddQtySubmitting(true);
      setError('');
      await axiosInstance.post(`/disposables/${disposableId}/add-quantity`, {
        quantity: qty,
        date_entered: addQtyForm.date_entered,
        rate: addQtyForm.rate !== '' ? addQtyForm.rate : null,
        vendor_id: parseInt(addQtyForm.vendor_id, 10)
      });
      setShowAddQty(false);
      setAddQtyForm({ quantity: '', date_entered: today(), rate: '', vendor_id: '' });
      await fetchDisposable();
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add stock');
    } finally {
      setAddQtySubmitting(false);
    }
  };

  const openEdit = () => {
    if (!disposable) return;
    setEditForm({
      disposable_name: disposable.disposable_name || '',
      rate: disposable.rate != null && disposable.rate !== '' ? String(disposable.rate) : '',
      date_entered: disposable.date_entered ? disposable.date_entered.slice(0, 10) : '',
      vendor_id: disposable.vendor_id != null ? String(disposable.vendor_id) : '',
      quantity: disposable.quantity != null ? String(disposable.quantity) : ''
    });
    setEditing(true);
    setError('');
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault();
    if (!editForm.disposable_name?.trim()) {
      setError('Name is required');
      return;
    }
    const qty = editForm.quantity !== '' ? parseInt(editForm.quantity, 10) : undefined;
    if (qty !== undefined && (Number.isNaN(qty) || qty < 0)) {
      setError('Quantity must be 0 or greater');
      return;
    }
    try {
      setEditSubmitting(true);
      setError('');
      await axiosInstance.put(`/disposables/${disposableId}`, {
        disposable_name: editForm.disposable_name.trim(),
        rate: editForm.rate !== '' ? editForm.rate : null,
        date_entered: editForm.date_entered || null,
        vendor_id: editForm.vendor_id ? parseInt(editForm.vendor_id, 10) : undefined,
        quantity: qty
      });
      setEditing(false);
      await fetchDisposable();
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update disposable');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleConsumedSubmit = async () => {
    const num = parseInt(consumeForm.qty, 10);
    if (!num || num <= 0) {
      setError('Enter a valid consumed quantity');
      return;
    }
    if (disposable && num > disposable.quantity) {
      setError('Consumed quantity cannot exceed current stock');
      return;
    }
    const raw = (consumeForm.datetime || nowDateTimeLocal()).trim();
    const datePart = raw.slice(0, 10);
    const todayStr = today();
    if (datePart > todayStr) {
      setError('Date cannot be in the future');
      return;
    }
    const consumptionDate = raw.includes('T')
      ? `${raw.slice(0, 10)} ${raw.slice(11, 16)}:00`
      : `${datePart} 00:00:00`;
    try {
      setConsuming(true);
      setError('');
      await axiosInstance.put(`/disposables/${disposableId}/quantity`, {
        consumedQty: num,
        consumptionDate
      });
      setConsumeModalOpen(false);
      setConsumeForm({ qty: '', datetime: nowDateTimeLocal() });
      await fetchDisposable();
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record consumption');
    } finally {
      setConsuming(false);
    }
  };

  const openHistoryEdit = (item) => {
    setEditingHistoryItem(item);
    let timeStr = '00:00';
    if (item.date) {
      const d = new Date(item.date);
      if (!Number.isNaN(d.getTime())) {
        timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
    }
    setHistoryEditForm({
      changeAmount: String(item.change_amount),
      date: item.date ? item.date.slice(0, 10) : today(),
      time: timeStr,
      changeNote: item.change_note || ''
    });
    setError('');
  };

  const handleHistoryEditSubmit = async (e) => {
    e?.preventDefault();
    if (!editingHistoryItem) return;
    const amount = parseInt(historyEditForm.changeAmount, 10);
    if (Number.isNaN(amount)) {
      setError('Quantity change must be a number');
      return;
    }
    try {
      setHistoryEditSubmitting(true);
      setError('');
      const datePart = historyEditForm.date || today();
      const timePart = (historyEditForm.time || '00:00').trim();
      const changeDateTime = timePart.match(/^\d{1,2}:\d{2}$/) ? `${datePart} ${timePart}:00` : `${datePart} 00:00:00`;
      await axiosInstance.put(
        `/disposables/${disposableId}/history/${editingHistoryItem.id}`,
        {
          changeAmount: amount,
          changeDate: changeDateTime,
          changeNote: historyEditForm.changeNote?.trim() || null
        }
      );
      setEditingHistoryItem(null);
      await fetchDisposable();
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update history');
    } finally {
      setHistoryEditSubmitting(false);
    }
  };

  const handleHistoryDelete = async () => {
    if (!historyDeleteConfirm) return;
    try {
      setError('');
      await axiosInstance.delete(`/disposables/${disposableId}/history/${historyDeleteConfirm.id}`);
      setHistoryDeleteConfirm(null);
      await fetchDisposable();
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete history');
    } finally {
      setHistoryDeleteConfirm(null);
    }
  };

  if (loading && !isPage) {
    return (
      <Modal open={true} onClose={onClose}>
        <ModalDialog>
          <Typography>Loading...</Typography>
        </ModalDialog>
      </Modal>
    );
  }

  const detailTitle = disposable?.disposable_name || 'Disposable';

  const detailBody = (
    <>
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography level="h3">
              Batch details
            </Typography>
            <Button
              size="sm"
              onClick={() => setShowAddQty((v) => !v)}
              sx={{
                backgroundColor: 'primary.500',
                '&:hover': { backgroundColor: 'primary.600' }
              }}
            >
              {showAddQty ? 'Cancel' : 'Add Stock'}
            </Button>
          </Box>

          {showAddQty && (
            <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'primary.50' }}>
              <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
                Add stock
              </Typography>
              <form onSubmit={handleAddQtySubmit}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                  <FormControl sx={{ minWidth: 160 }} required>
                    <FormLabel>Purchase Date</FormLabel>
                    <Input
                      type="date"
                      value={addQtyForm.date_entered}
                      onChange={(e) => setAddQtyForm((p) => ({ ...p, date_entered: e.target.value }))}
                      size="sm"
                      required
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 140 }}>
                    <FormLabel>Rate per item</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      value={addQtyForm.rate}
                      onChange={(e) => setAddQtyForm((p) => ({ ...p, rate: e.target.value }))}
                      size="sm"
                      required
                      placeholder="0"
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 120 }} required>
                    <FormLabel>Qty</FormLabel>
                    <Input
                      type="number"
                      min={1}
                      value={addQtyForm.quantity}
                      onChange={(e) => setAddQtyForm((p) => ({ ...p, quantity: e.target.value }))}
                      size="sm"
                      required
                      placeholder="0"
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <VendorSelect
                    vendors={vendors}
                    value={addQtyForm.vendor_id || ''}
                    onChange={(vendorId) => setAddQtyForm((p) => ({ ...p, vendor_id: vendorId }))}
                    onAddNew={() => setAddVendorOpen(true)}
                    loading={vendorsLoading}
                    required
                    sx={{ minWidth: 180, mb: 0 }}
                  />
                  <Button
                    type="submit"
                    size="sm"
                    loading={addQtySubmitting}
                    sx={{
                      backgroundColor: 'primary.500',
                      '&:hover': { backgroundColor: 'primary.600' }
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </form>
            </Card>
          )}

          <ResponsiveDetailTable
            mobile={isPage && disposable ? (
              <DetailFieldCard
                title="Current stock"
                fields={[
                  {
                    label: 'Purchase Date',
                    value: disposable.date_entered
                      ? new Date(disposable.date_entered).toLocaleDateString()
                      : 'N/A'
                  },
                  { label: 'Vendor', value: disposable.vendor_name || 'N/A' },
                  {
                    label: 'Rate',
                    value: disposable.rate != null
                      ? Number(disposable.rate).toLocaleString(undefined, { minimumFractionDigits: 2 })
                      : '—'
                  },
                  { label: 'Quantity', value: disposable.quantity },
                  {
                    label: 'Last Updated',
                    value: disposable.last_updated
                      ? new Date(disposable.last_updated).toLocaleDateString()
                      : 'N/A'
                  }
                ]}
                actions={(
                  <>
                    <Button size="sm" variant="outlined" color="neutral" onClick={openEdit}>Edit</Button>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => {
                        setConsumeModalOpen(true);
                        setConsumeForm({ qty: '', datetime: nowDateTimeLocal() });
                        setError('');
                      }}
                    >
                      Consumed
                    </Button>
                  </>
                )}
              />
            ) : null}
          >
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: 'primary.500',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: 'primary.50'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th>Purchase Date</th>
                  <th>Vendor</th>
                  <th>Rate (per item)</th>
                  <th>Current Quantity</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {disposable && (
                  <tr>
                    <td>
                      {disposable.date_entered
                        ? new Date(disposable.date_entered).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td>{disposable.vendor_name || 'N/A'}</td>
                    <td>
                      {disposable.rate != null
                        ? Number(disposable.rate).toLocaleString(undefined, { minimumFractionDigits: 2 })
                        : '—'}
                    </td>
                    <td>{disposable.quantity}</td>
                    <td>
                      {disposable.last_updated
                        ? new Date(disposable.last_updated).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="sm" variant="outlined" color="neutral" onClick={openEdit}>Edit</Button>
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          onClick={() => {
                            setConsumeModalOpen(true);
                            setConsumeForm({ qty: '', datetime: nowDateTimeLocal() });
                            setError('');
                          }}
                        >
                          Consumed
                        </Button>
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ResponsiveDetailTable>
        </Card>

        <Card>
          <Typography level="h3" sx={{ mb: 2 }}>
            Change History
          </Typography>
          <ResponsiveDetailTable
            mobile={isPage ? (
              history.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  No history found
                </Typography>
              ) : (
                history.map((item) => (
                  <DetailFieldCard
                    key={item.id}
                    title={item.date ? new Date(item.date).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    fields={[
                      { label: 'Vendor', value: item.vendor_name || 'N/A' },
                      { label: 'Type', value: item.change_note || (item.change_amount > 0 ? 'Added' : 'Consumed') },
                      {
                        label: 'Qty Change',
                        value: (
                          <Typography level="body-sm" sx={{ fontWeight: 600, color: item.change_amount > 0 ? 'success.600' : 'danger.500' }}>
                            {item.change_amount > 0 ? '+' : ''}{item.change_amount}
                          </Typography>
                        )
                      },
                      { label: 'Available', value: item.new_quantity }
                    ]}
                    actions={(
                      <>
                        <Button size="sm" variant="plain" color="neutral" onClick={() => openHistoryEdit(item)}>Edit</Button>
                        <Button size="sm" variant="plain" color="danger" onClick={() => setHistoryDeleteConfirm(item)}>Delete</Button>
                      </>
                    )}
                  />
                ))
              )
            ) : null}
          >
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: 'primary.500',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: 'primary.50'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Type</th>
                  <th>Quantity Change</th>
                  <th>Available Quantity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      No history found
                    </td>
                  </tr>
                ) : (
                  history.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.date
                          ? new Date(item.date).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                          : '—'}
                      </td>
                      <td>{item.vendor_name || 'N/A'}</td>
                      <td>{item.change_note || (item.change_amount > 0 ? 'Added' : 'Consumed')}</td>
                      <td
                        style={{
                          color: item.change_amount > 0 ? 'green' : 'red',
                          fontWeight: 'bold'
                        }}
                      >
                        {item.change_amount > 0 ? '+' : ''}
                        {item.change_amount}
                      </td>
                      <td>{item.new_quantity}</td>
                      <td>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => openHistoryEdit(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => setHistoryDeleteConfirm(item)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ResponsiveDetailTable>
        </Card>
    </>
  );

  return (
    <>
      {isPage ? (
        <StockDetailLayout title={detailTitle} onBack={onClose}>
          {loading ? <Typography>Loading...</Typography> : detailBody}
        </StockDetailLayout>
      ) : (
        <Modal open={true} onClose={onClose}>
          <ModalDialog
            sx={{
              maxWidth: '90vw',
              width: '1000px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography level="h2" sx={{ color: 'primary.500' }}>
                {detailTitle} - Details
              </Typography>
              <Button variant="plain" onClick={onClose}>Close</Button>
            </Box>
            {detailBody}
          </ModalDialog>
        </Modal>
      )}

        {editing && (
          <Modal open onClose={() => setEditing(false)}>
            <ModalDialog sx={{ maxWidth: 500 }}>
              <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
                Edit disposable
              </Typography>
              <form onSubmit={handleEditSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl required>
                    <FormLabel>Disposable Name</FormLabel>
                    <Input
                      value={editForm.disposable_name}
                      onChange={(e) => setEditForm((p) => ({ ...p, disposable_name: e.target.value }))}
                      size="sm"
                      required
                      placeholder="Name"
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Rate (per item)</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      value={editForm.rate}
                      onChange={(e) => setEditForm((p) => ({ ...p, rate: e.target.value }))}
                      size="sm"
                      required
                      placeholder="0"
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Purchase Date</FormLabel>
                    <Input
                      type="date"
                      value={editForm.date_entered}
                      onChange={(e) => setEditForm((p) => ({ ...p, date_entered: e.target.value }))}
                      size="sm"
                      required
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Current quantity (balance)</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      value={editForm.quantity}
                      onChange={(e) => setEditForm((p) => ({ ...p, quantity: e.target.value }))}
                      size="sm"
                      placeholder="0"
                      required
                      sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Vendor</FormLabel>
                    <Select
                      value={editForm.vendor_id || ''}
                      onChange={(_, val) => setEditForm((p) => ({ ...p, vendor_id: val ?? '' }))}
                      size="sm"
                      placeholder={vendorsLoading ? 'Loading...' : 'Select vendor'}
                      disabled={vendorsLoading}
                      sx={{ '&.Joy-focused': { borderColor: 'primary.500' } }}
                    >
                      {vendors.map((v) => (
                        <Option key={v.id} value={String(v.id)}>{v.name}</Option>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                  <Button type="button" variant="plain" color="neutral" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={editSubmitting}
                    sx={{
                      backgroundColor: 'primary.500',
                      '&:hover': { backgroundColor: 'primary.600' }
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </form>
            </ModalDialog>
          </Modal>
        )}

        {consumeModalOpen && (
          <Modal open onClose={() => setConsumeModalOpen(false)}>
            <ModalDialog sx={{ maxWidth: 400 }}>
              <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
                Consumed
              </Typography>
              <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                Current qty: {disposable?.quantity ?? 0}
              </Typography>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Quantity * (max {disposable?.quantity ?? 0})</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={disposable?.quantity ?? 0}
                  value={consumeForm.qty}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setConsumeForm((p) => ({ ...p, qty: '' }));
                      return;
                    }
                    const num = parseInt(raw, 10);
                    const max = Number(disposable?.quantity) || 0;
                    if (!Number.isNaN(num) && num > max) {
                      setConsumeForm((p) => ({ ...p, qty: String(max) }));
                    } else {
                      setConsumeForm((p) => ({ ...p, qty: raw }));
                    }
                  }}
                  placeholder="0"
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Date & time</FormLabel>
                <Input
                  type="datetime-local"
                  value={consumeForm.datetime}
                  max={`${today()}T23:59`}
                  onChange={(e) => setConsumeForm((p) => ({ ...p, datetime: e.target.value }))}
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button variant="plain" color="neutral" onClick={() => setConsumeModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  sx={{
                    backgroundColor: 'primary.500',
                    '&:hover': { backgroundColor: 'primary.600' }
                  }}
                  onClick={handleConsumedSubmit}
                  loading={consuming}
                  disabled={
                    (() => {
                      const n = parseInt(consumeForm.qty, 10);
                      return Number.isNaN(n) || n <= 0 || (disposable && n > Number(disposable.quantity));
                    })()
                  }
                >
                  Consumed
                </Button>
              </Box>
            </ModalDialog>
          </Modal>
        )}

        {/* Delete history confirmation */}
        <Modal
          open={Boolean(historyDeleteConfirm)}
          onClose={() => setHistoryDeleteConfirm(null)}
          sx={{ zIndex: 1400 }}
          slotProps={{
            backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } }
          }}
        >
          <ModalDialog
            layout="center"
            variant="soft"
            color="danger"
            sx={{ maxWidth: 400, boxShadow: 'lg', p: 3 }}
          >
            <Typography level="h4" sx={{ mb: 2 }}>
              Delete history record
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Delete this history record? Stock will be adjusted accordingly.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="plain" color="neutral" onClick={() => setHistoryDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button variant="solid" color="danger" onClick={handleHistoryDelete}>
                Delete
              </Button>
            </Box>
          </ModalDialog>
        </Modal>

        {/* Edit history modal */}
        {editingHistoryItem && (
          <Modal open onClose={() => setEditingHistoryItem(null)}>
            <ModalDialog sx={{ maxWidth: 440 }}>
              <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
                Edit history record
              </Typography>
              <form onSubmit={handleHistoryEditSubmit}>
                <FormControl sx={{ mb: 2 }} fullWidth>
                  <FormLabel>Quantity change * (negative = consumed, positive = added)</FormLabel>
                  <Input
                    type="number"
                    value={historyEditForm.changeAmount}
                    onChange={(e) => setHistoryEditForm((p) => ({ ...p, changeAmount: e.target.value }))}
                    required
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ mb: 2 }} fullWidth>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type="date"
                    value={historyEditForm.date}
                    onChange={(e) => setHistoryEditForm((p) => ({ ...p, date: e.target.value }))}
                    max={today()}
                    required
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ mb: 2 }} fullWidth>
                  <FormLabel>Time</FormLabel>
                  <Input
                    type="time"
                    value={historyEditForm.time}
                    onChange={(e) => setHistoryEditForm((p) => ({ ...p, time: e.target.value }))}
                    required
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ mb: 2 }} fullWidth>
                  <FormLabel>Type / Note</FormLabel>
                  <Input
                    value={historyEditForm.changeNote}
                    onChange={(e) => setHistoryEditForm((p) => ({ ...p, changeNote: e.target.value }))}
                    placeholder="e.g. Consumed, Added"
                    required
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button type="button" variant="plain" color="neutral" onClick={() => setEditingHistoryItem(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={historyEditSubmitting}
                    sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}
                  >
                    Save
                  </Button>
                </Box>
              </form>
            </ModalDialog>
          </Modal>
        )}

      <AddVendorModal
        open={addVendorOpen}
        onClose={() => setAddVendorOpen(false)}
        onCreated={handleVendorCreated}
      />
    </>
  );
};

export default DisposablePopup;

